import React, { Component, Fragment, useEffect } from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import {
    CatalogStepperIngredients,
    CatalogStepperOptions,
    DisplayableProductHeroImage,
    SelectableProductTile
} from 'app/ecommerce/common/components/catalog'
import { FormattedMessage } from 'react-intl'
import { H4 } from 'app/common/typography'
import {
    purchasable,
    updateIceOption,
    updateOptionsDisplayed,
    updatePickUpLaterOption,
    updateProductSelected,
    updateQuantityInitIngredient,
    updateQuantityInitOption
} from 'shared/utils/ecommerce/utils'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import type { Product } from 'shared/store/ecommerce/catalog'
import type { EventMetadata } from 'shared/store/metadata'
import { Check } from 'app/common/forms'
import { colors } from 'shared/assets/styles'
import { isDefined, orderPickUpType } from 'shared/utils'
import { AlertModal } from 'app/common/modals'

const { grey } = colors

const { TABLESERVICE, ONSITE, TABLE_ORDER } = orderPickUpType

type Props = {
    listProducts?: Array<?Product>,
    order: ?PurchasableItem,
    dispatchOrder: (order: ?PurchasableItem) => void,
    notLastCategory?: boolean,
    idProductGroup?: string,
    isPromotion?: boolean,
    featureExtraAvailable: boolean,
    featureCustoAvailable: boolean,
    featurePaidSideDishes?: boolean,
    sendMeta: EventMetadata => void,
    collectMode: string,
    noExtraPrice?: boolean
}

type State = {
    products: Array<?Product>,
    options: Array<any>,
    ingredients: Array<any>,
    showIceOption: ?boolean,
    showPickUpLaterOption: ?boolean,
    noIce: boolean
}

class CatalogProductGroup extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        const { listProducts } = props

        this.state = {
            products: listProducts || [],
            options: this.initializeOptions(),
            ingredients: this.initializeIngredients(),
            showIceOption: this.initializeIce(),
            noIce: this.initializeIceChoice(),
            showPickUpLaterOption: this.initializePickUpLater()
        }
    }

    initializeOptions = () => {
        const { order, listProducts } = this.props
        const test =
            (order
                ? this.onOptionsInit(true)
                : listProducts && listProducts.length === 1 && listProducts[0]
                ? updateOptionsDisplayed(listProducts, listProducts[0].id, true)
                : []) || []
        return test
    }

    initializeIngredients = () => {
        const { order, listProducts } = this.props
        return (
            (order
                ? this.onOptionsInit(false)
                : listProducts && listProducts.length === 1 && listProducts[0]
                ? updateOptionsDisplayed(
                      listProducts,
                      listProducts[0].id,
                      false
                  )
                : []) || []
        )
    }

    initializeIce = () => {
        const { order } = this.props
        return order && isDefined(order.noIce)
    }

    initializeIceChoice = () => {
        const { order } = this.props
        return !!order && isDefined(order.noIce) && !!order.noIce
    }

    initializePickUpLater = () => {
        const { order } = this.props
        return order && isDefined(order.pickUpLater)
    }

    initializePickUpLaterChoice = () => {
        const { order } = this.props
        return !!order && isDefined(order.pickUpLater) && !!order.pickUpLater
    }

    componentDidUpdate(prevProps: Props) {
        const { listProducts, order } = this.props
        if (prevProps.listProducts !== listProducts && listProducts) {
            this.setState({ products: listProducts })
        }
        if (
            prevProps.order !== order &&
            (_.isEmpty(order) ||
                (order &&
                    (!order.customizedIngredients ||
                        order.customizedIngredients.length === 0) &&
                    (!order.options || order.options.length === 0)))
        ) {
            this.setState({
                options: this.initializeOptions(),
                ingredients: this.initializeIngredients()
            })
        }
    }

    onOptionsInit = (isOption: boolean) => {
        const { order, listProducts } = this.props
        if (order && order.id) {
            const options = updateOptionsDisplayed(
                listProducts,
                order.id,
                isOption
            )
            if (options) {
                if (isOption) {
                    //$FlowFixMe
                    return updateQuantityInitOption(options, order)
                } else {
                    //$FlowFixMe
                    return updateQuantityInitIngredient(options, order)
                }
            }
        }
        return []
    }

    onProductSelected = (idProduct: string, isSelected: boolean) => {
        const { products, noIce } = this.state
        const { dispatchOrder, idProductGroup, collectMode } = this.props
        const canPickUpLater = [TABLESERVICE, ONSITE, TABLE_ORDER].includes(
            collectMode
        )
        this.setState({
            products: updateProductSelected(products, idProduct, isSelected),
            options: [],
            ingredients: []
        })
        if (isSelected) {
            this.setState({
                options:
                    updateOptionsDisplayed(products, idProduct, true) || [],
                ingredients:
                    updateOptionsDisplayed(products, idProduct, false) || [],
                showIceOption: updateIceOption(products, idProduct),
                showPickUpLaterOption:
                    updatePickUpLaterOption(products, idProduct) &&
                    canPickUpLater
            })
            let product =
                products && products.find(e => e && e.id === idProduct)
            if (product && product.hasIce) product = { ...product, noIce }
            if (product && product.hasPickUpLater && canPickUpLater)
                product = {
                    ...product,
                    pickUpLater: this.initializePickUpLaterChoice()
                }
            dispatchOrder(purchasable(product || undefined, idProductGroup))
        } else {
            this.setState({
                options: [],
                ingredients: [],
                showIceOption: false,
                showPickUpLaterOption: false
            })
            //$FlowFixMe
            dispatchOrder({})
        }
    }

    onPickUpLaterClick = (value: boolean) => {
        this.updatePickUpLaterChoice(value)
        this.setState({
            showPickUpLaterOption: false
        })
    }

    updateIceChoice = async () => {
        const { order, dispatchOrder } = this.props
        await this.setState({ noIce: !this.state.noIce })
        dispatchOrder({ ...order, noIce: this.state.noIce })
    }

    updatePickUpLaterChoice = (value: boolean) => {
        const { order, dispatchOrder } = this.props
        dispatchOrder({ ...order, pickUpLater: value })
    }

    componentDidMount() {
        if (this.state.products.length === 1) {
            this.onProductSelected(this.state.products[0].id, true)
        }
    }

    render() {
        const {
            products,
            options,
            ingredients,
            showIceOption,
            showPickUpLaterOption,
            noIce
        } = this.state
        const {
            order,
            dispatchOrder,
            notLastCategory,
            isPromotion,
            featureCustoAvailable,
            featureExtraAvailable,
            sendMeta,
            featurePaidSideDishes,
            noExtraPrice
        } = this.props

        if (!products) {
            return null
        }

        const displayIngredients =
            (featureExtraAvailable &&
                ingredients.findIndex(product => !!product.price) >= 0) ||
            (featureCustoAvailable &&
                ingredients.findIndex(product => !product.price) >= 0)

        return (
            <Fragment>
                {products.length > 1 ? (
                    <div>
                        <TilesContainer
                            hasOption={
                                notLastCategory ? true : options.length > 0
                            }>
                            {products.map(
                                //$FlowFixMe
                                (
                                    {
                                        id,
                                        name,
                                        image,
                                        routeId,
                                        isSelected,
                                        available,
                                        type,
                                        menuExtraPriceTTC,
                                        hasSelectedAllergens,
                                        composable
                                    },
                                    index
                                ) => (
                                    <SelectableProductTile
                                        key={index}
                                        id={id}
                                        name={name}
                                        type={type}
                                        imageUrl={image}
                                        routeId={routeId}
                                        isSelected={isSelected}
                                        hasAllergens={hasSelectedAllergens}
                                        onTileSelected={(
                                            idProduct,
                                            isSelected
                                        ) =>
                                            this.onProductSelected(
                                                idProduct,
                                                isSelected
                                            )
                                        }
                                        available={available}
                                        menuExtraPriceTTC={
                                            !noExtraPrice && menuExtraPriceTTC
                                        }
                                    />
                                )
                            )}
                        </TilesContainer>
                        {showIceOption && (
                            <IceContainer>
                                <Check
                                    labelKey="clickandcollect.catalog.drink.with.ice"
                                    checked={!noIce}
                                    color={grey}
                                    onClick={this.updateIceChoice}
                                />
                            </IceContainer>
                        )}
                        {showPickUpLaterOption && (
                            <AlertModal
                                isOpen={showPickUpLaterOption}
                                handleClose={() =>
                                    this.setState({
                                        showPickUpLaterOption: false
                                    })
                                }
                                alertTitleKey={
                                    'ecommerce.catalog.pick.up.later'
                                }
                                alertContentKey={
                                    'ecommerce.catalog.pick.up.modal.content'
                                }
                                okLabel={'yes'}
                                leftLabel={'no'}
                                onLeftClick={() =>
                                    this.onPickUpLaterClick(false)
                                }
                                onOkClick={() => this.onPickUpLaterClick(true)}
                            />
                        )}
                    </div>
                ) : (
                    <DisplayableProductHeroImage
                        imageUrl={products[0] && products[0].image}
                    />
                )}
                {options.length > 0 && (
                    <StepperContainer
                        hasMargin={notLastCategory && ingredients.length > 0}>
                        <FormattedMessage id="clickandcollect.catalog.sauce">
                            {(label: string) => <StyledH4>{label}</StyledH4>}
                        </FormattedMessage>
                        <CatalogStepperOptions
                            listOptions={options}
                            dispatchOrder={dispatchOrder}
                            order={order}
                        />
                    </StepperContainer>
                )}
                {displayIngredients && (
                    <StepperContainer hasMargin={!notLastCategory}>
                        <FormattedMessage id="clickandcollect.catalog.menu.options">
                            {(label: string) => <StyledH4>{label}</StyledH4>}
                        </FormattedMessage>
                        <CatalogStepperIngredients
                            listIngredients={ingredients}
                            dispatchOrder={dispatchOrder}
                            order={order}
                            featureCustoAvailable={featureCustoAvailable}
                            featureExtraAvailable={featureExtraAvailable}
                            sendMeta={sendMeta}
                        />
                    </StepperContainer>
                )}
            </Fragment>
        )
    }
}

const TilesContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: ${props => (props.hasOption ? `20px` : `100px`)};
`
const StepperContainer = styled.div`
    margin-bottom: ${props => (props.hasMargin ? `100px` : `20px`)};
`
const StyledH4 = styled(H4)`
    margin-vertical: 20px;
    align-self: center;
`
const IceContainer = styled.div`
    margin-left: calc(50% - 70px);
`
export { CatalogProductGroup }
